// Allotment Letter (M8-F7 / F8; rows M8.11 / M8.21 / M8.22 / M8.24; DC-74 / DC-143 / DC-168), prototype mLetter.
// The letter as a page (10-minute link), "Who will sign?" (any one applicant), "Signed by VJ", the NRI note, then
// Sign with Aadhaar: the customer leaves for Surepass and comes back to /Home/Dashboard?crm2LetterEsign=<booking>, where
// the card checks the result (Crm2BookingCard). The Aadhaar name must match the applicant chosen; a mismatch shows here
// with the name that came back. After the eSign: the signed line, the lock notice and Download PDF of the signed copy.
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import { getAllotmentLetter, allotmentLetterLink, startLetterEsign } from "../../../Services/Crm2Portal";
import { errorTost } from "../../../Services/Toast";
import { niceDate } from "./Crm2Parts";
import css from "./Crm2Booking.module.css";

const panel = { border: "1px solid #EAECF0", borderRadius: ".8rem", padding: "1.2rem 1.4rem", marginTop: "1rem", fontSize: "1.4rem" };
const h4 = { margin: "0 0 .6rem", fontSize: "1.5rem", color: "#101828" };
const btn = (primary) => ({ border: primary ? "1px solid #155EEF" : "1px solid #D0D5DD", background: primary ? "#155EEF" : "#FFFFFF", color: primary ? "#FFFFFF" : "#344054", borderRadius: ".8rem", padding: ".9rem 1.6rem", fontSize: "1.4rem", cursor: "pointer" });

export default function AllotmentLetterModal({ allotment, onClose }) {
	const apId = allotment.allotmentPaymentId;
	const [s, setS] = useState(null);
	const [url, setUrl] = useState(null);
	const [failed, setFailed] = useState("");
	const [signer, setSigner] = useState(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		getAllotmentLetter(apId)
			.then((v) => {
				setS(v);
				if (v.applicants?.length === 1) setSigner(v.applicants[0].applicantId);
				return allotmentLetterLink(apId, v.status === "SIGNED" ? "signed" : "letter").then((l) => setUrl(l.url));
			})
			.catch((err) => {
				console.error("[crm2][AllotmentLetterModal] load failed", err);
				setFailed(err.message || "Could not load your allotment letter. Please try again.");
			});
	}, [apId]);

	const sign = async () => {
		if (!signer) return errorTost("Choose who will sign");
		setBusy(true);
		try {
			const r = await startLetterEsign(apId, signer);
			console.log("[crm2][AllotmentLetterModal] going to Surepass", { apId, attemptNo: r.attemptNo, reused: r.reused });
			window.location.href = r.url;
		} catch (err) {
			setBusy(false);
			errorTost(err.message || "The eSign could not be started. Please try again.");
		}
	};

	const signed = s?.status === "SIGNED";
	const refused = s?.status === "REFUSED";
	const last = s?.lastAttempt;
	return (
		<Modal open onClose={(_, r) => r !== "backdropClick" && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
			<Box className={`${css.modal} ${css.modalWide}`}>
				<div className={css.modalHeader}>
					<span>Allotment Letter</span>
					<span className={css.modalClose} onClick={onClose} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>
				<div className={css.modalContent}>
					{failed && <div className={css.badBox}>{failed}</div>}
					{!s && !failed && <p className={css.muted}>Loading…</p>}
					{s && (
						<>
							{url ? <iframe className={css.docFrame} title="Allotment Letter" src={url} /> : <p className={css.muted}>The letter could not be shown. Please try again.</p>}
							{signed ? (
								<div style={panel}>
									<h4 style={h4}>Signed</h4>
									<div className={css.good}>
										Signed with Aadhaar eSign on {niceDate(s.signed.at)} by {s.signed.name}
										{s.signed.last4 ? ` · Aadhaar XXXX ${s.signed.last4}` : ""}.
									</div>
									<p className={css.muted} style={{ marginTop: ".8rem" }}>
										{s.lockLine}
									</p>
								</div>
							) : (
								<>
									{refused && (
										<div className={css.badBox} style={{ marginTop: "1rem" }}>
											{last?.aadhaarName
												? `Signature refused: the name on Aadhaar (${last.aadhaarName}) does not match ${last.applicantName}. Please sign again as the applicant you choose, or ask your CRM.`
												: `Signature refused: we did not receive the Aadhaar name of ${last?.applicantName}. Please sign again, or ask your CRM.`}
										</div>
									)}
									{last?.status === "PENDING" && <div className={css.info} style={{ marginTop: "1rem" }}>{last.applicantName} started the Aadhaar eSign. If you finished it, it shows here in a few minutes.</div>}
									<div style={panel}>
										<h4 style={h4}>Who will sign?</h4>
										{(s.applicants || []).map((a) => (
											<label key={a.applicantId} style={{ display: "flex", gap: ".8rem", alignItems: "center", padding: ".6rem 0", cursor: "pointer" }}>
												<input type="radio" name="crm2-letter-signer" checked={signer === a.applicantId} onChange={() => setSigner(a.applicantId)} />
												<span>
													<b>{a.name}</b>
													<br />
													<span className={css.muted}>
														{a.no === 1 ? "Primary applicant" : `Applicant ${a.no}`} · OTP to {a.maskedMobile}
													</span>
												</span>
											</label>
										))}
										<div className={css.muted}>Any one applicant signs with Aadhaar OTP. The Aadhaar name must match the applicant you choose.</div>
									</div>
									<div style={panel}>
										<h4 style={h4}>Signed by VJ</h4>
										<div className={css.muted}>This letter is already signed by VJ's authorised signatory. Only your Aadhaar eSign is needed.</div>
									</div>
									<div style={panel}>
										<h4 style={h4}>NRI?</h4>
										<div className={css.muted}>Aadhaar eSign is for Indian residents. NRI customers: contact your CRM.</div>
									</div>
								</>
							)}
							<div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", marginTop: "1.4rem", flexWrap: "wrap" }}>
								{url && (
									<a href={url} target="_blank" rel="noreferrer" style={{ ...btn(false), textDecoration: "none" }}>
										Download PDF
									</a>
								)}
								{signed ? (
									<button type="button" style={btn(true)} onClick={onClose}>
										Close
									</button>
								) : (
									<button type="button" style={{ ...btn(true), opacity: s.canSign && !busy ? 1 : 0.5 }} disabled={!s.canSign || busy} onClick={sign}>
										{busy ? "Opening Aadhaar eSign…" : "Sign with Aadhaar"}
									</button>
								)}
							</div>
						</>
					)}
				</div>
			</Box>
		</Modal>
	);
}
